<script setup>
import InputLabel from "@/Components/InputLabel.vue";
import TextInput from "@/Components/TextInput.vue";
import { ref, watch, computed } from "vue";
import { Inertia } from "@inertiajs/inertia";
import { useHelpers } from "@/helpers";
import { usePage } from "@inertiajs/inertia-vue3";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import SearchSelect from "@/Components/SearchSelect.vue";
import SelectInput from "@/Components/SelectInput.vue";
import _ from "lodash";

const helpers = useHelpers();
const currentUser = usePage().props.value.auth.user;

const props = defineProps({
  companies: Array,
  filters: Object,
  clients: Array,
});

const clientNameRef = ref(null);
const projectNameRef = ref(null);
const quoteNumberRef = ref(null);

const defaultFilters = {
  client: null,
  company: null,
  projectName: null,
  quoteNumber: null,
}

const filterValues = ref(Object.assign({}, defaultFilters, props.filters || {}));
const searching = ref(false);

watch(
  () => props.filters,
  (filters) => {
    filterValues.value = Object.assign({}, defaultFilters, filters || {});
  },
  { deep: true }
);

const clientOptions = computed(() =>
  (props.clients || [])
    .filter((client) => client?.client && (client?.ID != null || client?.id != null))
    .map((client) => ({
      label: client.client ?? client.name,
      value: Number(client.ID ?? client.id),
    }))
);

const clientModel = computed({
  get: () => (
    filterValues.value.client != null && filterValues.value.client !== ''
      ? Number(filterValues.value.client)
      : null
  ),
  set: (v) => {
    filterValues.value.client = v != null ? Number(v) : null;
  },
});

const resetFilters = () => {
  searching.value = true;
  Inertia.get(route(route().current(), {}), {}, { preserveScroll: true });
  searching.value = false;
};

const makeSearchRequest = _.debounce(() => executeSearchRequest(), 400);

const executeSearchRequest = () => {
  searching.value = true;
  const queryParameters = route().params || {};
  if (queryParameters.page) {
    delete queryParameters.page;
  }
  queryParameters['filters'] = filterValues.value;

  Inertia.get(route(route().current(), queryParameters), {}, {
    preserveScroll: true,
    preserveState: true,
    onFinish() {
      searching.value = false;
    }
  });
}
</script>

<template>
  <div>
    <InputLabel for="client-name" value="Client" />
    <SearchSelect v-model="clientModel" :options="clientOptions" placeholder="Client" class="mt-1 w-full"
      :clearable="true" :disabled="searching" @change="makeSearchRequest" />
  </div>
  <div v-if="!helpers.userIsVendor(currentUser)">
    <InputLabel for="company-name" value="Company Name" />
    <SelectInput id="company-name" class="mt-1"
      v-model="filterValues.company" @change="makeSearchRequest" :disabled="searching">
      <option :value="null">Please select</option>
      <option v-for="company in companies" :value="company.company_id" v-text="company.company"></option>
    </SelectInput>
  </div>
  <div v-if="!helpers.userIsVendor(currentUser)">
    <InputLabel for="project-name" value="Project Name" />
    <TextInput id="project-name" type="text" class="mt-1 w-full" placeholder="Project Name" ref="projectNameRef"
      v-model="filterValues.projectName" @keyup="makeSearchRequest" />
  </div>
  <div>
    <InputLabel for="quote-number" value="Quote Number" />
    <TextInput id="quote-number" type="text" class="mt-1 w-full" placeholder="Quote Number" ref="quoteNumberRef"
      v-model="filterValues.quoteNumber" @keyup="makeSearchRequest" />
  </div>

  <div>
    <InputLabel value="Reset Filter" class="text-gray-100" />
    <SecondaryButton @click="resetFilters" class="mt-1 h-[42px]">Reset Filters</SecondaryButton>
  </div>
</template>
